import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { Routes } from '../types/enums/Routes.enum';
import { storage } from '../utils/Const.utils';
import { StorageKeys } from '../types/enums/Storage.enum';

/**
 * Singleton para verificación periódica de tokens
 * Evita múltiples intervals cuando hay múltiples instancias del hook
 */
let tokenCheckInterval: NodeJS.Timeout | null = null;
let activeInstances = 0;
let refreshTokenCallback: (() => Promise<void>) | null = null;

/**
 * Hook personalizado para autenticación con funcionalidades adicionales
 */
export const useAuthHook = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Verificar si el usuario está autenticado y el token es válido
   */
  const isValidAuthentication = useCallback((): boolean => {
    return auth.state.isAuthenticated && !auth.isTokenExpired();
  }, [auth.state.isAuthenticated, auth.isTokenExpired]);

  /**
   * Redirigir al login si no está autenticado
   */
  const requireAuth = useCallback(() => {
    if (!isValidAuthentication()) {
      /* Guardar la ruta actual para redirigir después del login */
      const currentPath = location.pathname + location.search;
      const redirectTo = currentPath !== Routes.LOGIN ? currentPath : Routes.DEFAULT;
      
      navigate(Routes.LOGIN, { 
        replace: true, 
        state: { from: redirectTo } 
      });
      return false;
    }

    /* Verificar si el usuario necesita completar la selección de rol post-login */
    const hasCompletedRolSelection = storage.get(StorageKeys.PERFIL);
    const currentPath = location.pathname;
    
    if (!hasCompletedRolSelection && currentPath !== Routes.POST_LOGIN_ROL) {
      navigate(Routes.POST_LOGIN_ROL, { 
        replace: true, 
        state: { from: currentPath } 
      });
      return false;
    }

    return true;
  }, [isValidAuthentication, navigate, location]);

  /**
   * Logout con redirección automática
   */
  const handleLogout = useCallback(async () => {
    /* Limpiar datos de selección de rol */
    storage.remove(StorageKeys.PERFIL);
    
    await auth.logout();
    navigate(Routes.LOGIN, { replace: true });
  }, [auth, navigate]);

  /**
   * Login con redirección automática a la selección de rol post-login
   */
  const handleLoginSuccess = useCallback((loginData: any) => {
    auth.login(loginData);
    
    /* Redirigir a la página de selección de rol después del login exitoso */
    navigate(Routes.POST_LOGIN_ROL, { 
      replace: true,
      state: { 
        from: location.state?.from || Routes.DEFAULT,
        loginData 
      }
    });
  }, [auth, navigate, location.state]);

  /**
   * Verificar permisos para una funcionalidad específica
   */
  const checkPermission = useCallback((modulo: string, permiso?: string): boolean => {
    if (!isValidAuthentication()) {
      return false;
    }
    return auth.hasPermission(modulo, permiso);
  }, [auth, isValidAuthentication]);

  /**
   * Verificar si el usuario tiene al menos uno de los permisos especificados
   */
  const hasAnyPermission = useCallback((permissions: Array<{ modulo: string; permiso?: string }>): boolean => {
    if (!isValidAuthentication()) {
      return false;
    }
    return permissions.some(({ modulo, permiso }) => auth.hasPermission(modulo, permiso));
  }, [auth, isValidAuthentication]);

  /**
   * Obtener información del usuario actual
   */
  const getCurrentUser = useCallback(() => {
    return isValidAuthentication() ? auth.state.user : null;
  }, [auth.state.user, isValidAuthentication]);

  /**
   * Escuchar evento de logout desde Http.service
   */
  useEffect(() => {
    const handleAuthLogout = () => {
      if (location.pathname !== Routes.LOGIN && !location.pathname.startsWith(Routes.REGISTRO)) {
        navigate(Routes.LOGIN, { replace: true });
      }
    };

    window.addEventListener('auth:logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, [navigate, location.pathname]);

  /**
   * Intentar refrescar el token al cambiar de ruta (sin hacer logout)
   * Solo hace refresh silencioso para mantener la sesión activa
   */
  useEffect(() => {
    if (auth.state.isAuthenticated && auth.isTokenExpired()) {
      /* 
        Refresh silencioso - no hace logout si falla
        El sistema de detección de inactividad se encarga de logout por inactividad
       */
      auth.refreshToken().catch((error) => {
        /* No hacer logout aquí - dejar que el sistema de inactividad lo maneje */
      });
    }
  }, [location.pathname, auth]);

  /**
   * Verificación periódica consolidada con singleton
   * Solo una instancia del interval se ejecuta independientemente de cuántos componentes usen el hook
   */
  useEffect(() => {
    if (!auth.state.isAuthenticated) return;

    // Incrementar contador de instancias activas
    activeInstances++;
    refreshTokenCallback = auth.refreshToken;

    // Solo crear el interval si no existe
    if (!tokenCheckInterval) {
      const checkTokenPeriodically = () => {
        if (auth.isTokenExpired() && refreshTokenCallback) {
          refreshTokenCallback().catch((error) => {
            /* El refresh ya maneja el logout después de reintentos */
          });
        }
      };

      // Verificar cada 5 minutos
      tokenCheckInterval = setInterval(checkTokenPeriodically, 5 * 60 * 1000);
      console.info('Intervalo de verificación de token iniciado (singleton)');
    }

    return () => {
      // Decrementar contador de instancias
      activeInstances--;

      // Solo limpiar el interval cuando no hay más instancias activas
      if (activeInstances === 0 && tokenCheckInterval) {
        clearInterval(tokenCheckInterval);
        tokenCheckInterval = null;
        refreshTokenCallback = null;
        console.log('Intervalo de verificación de token detenido (todas las instancias desmontadas)');
      }
    };
  }, [auth.state.isAuthenticated, auth.refreshToken, auth.isTokenExpired]);

  return {
    /* Estado */
    user: auth.state.user,
    isAuthenticated: auth.state.isAuthenticated,
    isLoading: auth.state.isLoading,
    accessToken: auth.state.accessToken,
    
    /* Funciones de autenticación */
    login: handleLoginSuccess,
    logout: handleLogout,
    requireAuth,
    
    /* Verificaciones */
    isValidAuthentication,
    isTokenExpired: auth.isTokenExpired,
    
    /* Permisos */
    hasPermission: checkPermission,
    hasAnyPermission,
    
    /* Utilidades */
    getCurrentUser,
  };
};
